'use client'

import * as Accordion from '@radix-ui/react-accordion'
import { refreshQrList } from 'features/library/client'
import { useSetAtom } from 'jotai'
import { RESET, useHydrateAtoms } from 'jotai/utils'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { debounce } from 'radash'
import { useEffect, useState } from 'react'
import { useBus, useListener } from 'react-bus'

import { showNotification } from '@/components/Notification'
import { ROUTES } from '@/constants'
import { isAuthenticatedAtom } from '@/global-state'
import { getPathname, useRouter } from '@/i18n-routing'
import { getStore } from '@/store'
import { getIsSocialAuthenticated } from '@/utils/user/lastAuthProvider'

import { captureBuilderEvent } from '../../builderAnalytics'
import {
	GeneratorFormSection,
	TGeneratorFormSection,
} from '../../components/GeneratorFormSection'
import {
	NEXT_BUTTON_CLICK_EVENT,
	RESET_NEXT_BUTTON_LOADING_EVENT,
} from '../../GeneratorLayout/GeneratorFooter/NextButton'
import { qrPreviewDesignAtom } from '../../GeneratorLayout/qrPreviewDesignState'
import {
	activeContentTypeAtom,
	getBuilderSessionDurationSec,
	isSignUpDialogOpenAtom,
	qrTypeAtom,
	qrUriAtom,
	setOnboardingQrId,
	signUpDefaultEmailAtom,
} from '../../generatorState'
import {
	GENERATOR_SEARCH_PARAM,
	QR_TYPE,
	TQrContent,
} from '../../generatorTypes'
import type { TQrDesign } from '../../services/types'
import { getGeneratorSearchParam } from '../../utils/generatorSearchParam'
import { publishEditedQr } from '../../utils/publishEditedQr'
import { QR_TYPE_TO_CONTENT_ATOM } from '../../utils/qrTypeToContentAtom'
import {
	cornerDotColorAtom,
	cornerDotStyleAtom,
	cornerFrameColorAtom,
	cornerFrameStyleAtom,
	useCornerDesignForm,
} from './CornersDesignForm'
import {
	frameBackgroundColorAtom,
	frameColorAtom,
	frameTextAtom,
	frameTextColorAtom,
	frameTypeAtom,
	useFrameDesignForm,
} from './FrameDesignForm'
import { clearGuestDraftPassword } from './guestDraftPasswordStorage'
import {
	qrLogoImageAtom,
	qrLogoImageFileAtom,
	qrSelectedLogoUrlAtom,
	useLogoDesignForm,
} from './LogoDesignForm'
import { usePasswordDesignForm } from './PasswordDesignForm'
import {
	draftPasswordAtom,
	PASSWORD_DESIGN_SECTION_ID,
	passwordAtom,
	passwordEnabledAtom,
	passwordLoadedForTargetAtom,
	passwordRepeatAtom,
	passwordSubmitAttemptedAtom,
	savedPasswordAtom,
} from './passwordDesignState'
import {
	patternAtom,
	qrBackgroundColorAtom,
	qrDotColorAtom,
	usePatternDesignForm,
} from './PatternDesignForm'
import {
	buildDashboardWithNewQrPopupUrl,
	publishNewQr,
	putCurrQr,
} from './qrDesignFormUtils'
import { qrDesignAtom } from './qrDesignState'
import {
	isQrPasswordBlockingFinish,
	syncBuilderQrPassword,
} from './qrPasswordUtils'

export const resetQrDesignForm = () => {
	// Frame section
	getStore().set(frameTypeAtom, RESET)
	getStore().set(frameTextAtom, RESET)
	getStore().set(frameColorAtom, RESET)
	getStore().set(frameBackgroundColorAtom, RESET)
	getStore().set(frameTextColorAtom, RESET)
	// Pattern section
	getStore().set(patternAtom, RESET)
	getStore().set(qrDotColorAtom, RESET)
	getStore().set(qrBackgroundColorAtom, RESET)
	// Corners section
	getStore().set(cornerFrameStyleAtom, RESET)
	getStore().set(cornerFrameColorAtom, RESET)
	getStore().set(cornerDotColorAtom, RESET)
	getStore().set(cornerDotStyleAtom, RESET)
	// Logo section
	getStore().set(qrSelectedLogoUrlAtom, RESET)
	getStore().set(qrSelectedLogoUrlAtom, RESET)
	getStore().set(qrLogoImageAtom, RESET)
	getStore().set(qrLogoImageFileAtom, RESET)
	// Password section
	getStore().set(passwordEnabledAtom, RESET)
	getStore().set(passwordAtom, RESET)
	getStore().set(passwordRepeatAtom, RESET)
	getStore().set(passwordSubmitAttemptedAtom, RESET)
	getStore().set(savedPasswordAtom, RESET)
	getStore().set(passwordLoadedForTargetAtom, RESET)
	getStore().set(draftPasswordAtom, RESET)
	clearGuestDraftPassword()
}

export const QrDesignFormScreen = ({
	qrId,
	qrUri,
	qrContent,
	qrDesign,
	isNewUser,
	isNeverPaidUser,
	defaultEmail,
}: {
	qrId: string
	qrUri: string
	qrContent: TQrContent
	qrDesign: TQrDesign | null
	isNewUser: boolean
	isNeverPaidUser: boolean
	defaultEmail: string
}) => {
	const t = useTranslations()
	const locale = useLocale()
	const router = useRouter()
	const searchParams = useSearchParams()
	const bus = useBus()

	const qrType = qrContent.type
	const setQrType = useSetAtom(qrTypeAtom)
	const setActiveContentType = useSetAtom(activeContentTypeAtom)
	const setSignUpDefaultEmail = useSetAtom(signUpDefaultEmailAtom)
	const setIsSignUpDialogOpen = useSetAtom(isSignUpDialogOpenAtom)
	const isEditMode = searchParams.get(GENERATOR_SEARCH_PARAM.Target)

	useEffect(() => {
		setQrType(qrType)
		setActiveContentType(qrType)
	}, [setQrType, setActiveContentType, qrType])

	useEffect(() => {
		if (defaultEmail) {
			setSignUpDefaultEmail((current) => {
				if (current?.qrId === qrId) {
					return {
						...current,
						previousEmail: current.previousEmail ?? defaultEmail,
						draftEmail: current.draftEmail ?? defaultEmail,
					}
				}

				return {
					qrId,
					previousEmail: defaultEmail,
					draftEmail: defaultEmail,
				}
			})
		}
	}, [defaultEmail, isNeverPaidUser, isNewUser, qrId, setSignUpDefaultEmail])

	const contentAtom = QR_TYPE_TO_CONTENT_ATOM[qrType]

	useHydrateAtoms([
		[qrUriAtom, qrUri],
		[contentAtom, qrContent],
		[qrDesignAtom, qrDesign],
	])

	// After sign-up → checkout, UseResetGeneratorForms may have cleared content atoms
	useEffect(() => {
		const resyncContentFromProps = () => {
			const store = getStore()

			store.set(qrUriAtom, qrUri)
			store.set(QR_TYPE_TO_CONTENT_ATOM[qrContent.type], qrContent)
			store.set(qrDesignAtom, qrDesign)
			setQrType(qrContent.type)
			setActiveContentType(qrContent.type)
		}

		resyncContentFromProps()

		const handlePageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				resyncContentFromProps()
			}
		}

		window.addEventListener('pageshow', handlePageShow)
		return () => window.removeEventListener('pageshow', handlePageShow)
	}, [qrContent, qrDesign, qrUri, setQrType, setActiveContentType])

	const formSectionList: TGeneratorFormSection[] = [
		useFrameDesignForm(),
		usePatternDesignForm(),
		useCornerDesignForm(),
		useLogoDesignForm(),
		usePasswordDesignForm(),
	].filter(
		({ id }) =>
			id !== PASSWORD_DESIGN_SECTION_ID || qrType !== QR_TYPE.Wifi,
	)

	const [openSectionList, setOpenSectionList] = useState(
		formSectionList?.[0]?.id ? [formSectionList[0].id] : [],
	)

	const handleSubmit = async () => {
		try {
			const isAuthenticated = getStore().get(isAuthenticatedAtom)
			const qrId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)
			const isOnboardingUser = isNewUser || isNeverPaidUser
			const skipSignUpStep = isAuthenticated && getIsSocialAuthenticated()
			if (isQrPasswordBlockingFinish()) {
				getStore().set(passwordSubmitAttemptedAtom, true)
				setOpenSectionList((prev) =>
					prev.includes(PASSWORD_DESIGN_SECTION_ID)
						? prev
						: [...prev, PASSWORD_DESIGN_SECTION_ID],
				)
				bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
				return
			}
			await putCurrQr(qrType)
			if (!isEditMode && qrId) {
				await syncBuilderQrPassword({ qrId, isAuthenticated })
			}
			if (
				!isEditMode &&
				!skipSignUpStep &&
				(!isAuthenticated || isOnboardingUser)
			) {
				if (!qrId) {
					throw new Error(
						'Failed setting onboarding QR ID, missing QR ID in URL.',
					)
				}
				setOnboardingQrId(qrId)
				setIsSignUpDialogOpen(true)
				captureBuilderEvent('builder_funnel_complete', {
					duration_sec: getBuilderSessionDurationSec(),
				})
				// Not router.push: routing here remounts this screen, and Back
				// then replays a stale RSC payload over the saved design.
				window.history.pushState(
					window.history.state,
					'',
					`${getPathname({
						locale,
						href: ROUTES.generator.signup,
					})}?id=${qrId}`,
				)
				bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
				return
			}
			if (isAuthenticated) {
				if (isEditMode) {
					const qrId = getGeneratorSearchParam(
						GENERATOR_SEARCH_PARAM.Target,
					)
					await publishEditedQr(qrType)
					router.push(buildDashboardWithNewQrPopupUrl(qrId!, 'edit'))
				} else {
					await publishNewQr()
					const qrId = getGeneratorSearchParam(
						GENERATOR_SEARCH_PARAM.Id,
					)
					if (isOnboardingUser) {
						if (!qrId) {
							throw new Error(
								'Failed setting onboarding QR ID, missing QR ID in URL.',
							)
						}
						setOnboardingQrId(qrId)
					}
					captureBuilderEvent('builder_funnel_complete', {
						duration_sec: getBuilderSessionDurationSec(),
					})
					router.push(
						isOnboardingUser
							? ROUTES.checkout.index
							: buildDashboardWithNewQrPopupUrl(qrId!, 'create'),
					)
				}
				await refreshQrList()
			}
		} catch (error) {
			console.error('Failed publishing QR code', error)
			void showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
			bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
		}
	}

	useListener(NEXT_BUTTON_CLICK_EVENT, handleSubmit)

	useEffect(() => {
		const unsubscribe = getStore().sub(
			qrPreviewDesignAtom,
			debounce({ delay: 1_000 }, async () => {
				try {
					await putCurrQr(qrType)
				} catch (error) {
					console.error('Saving QR automatically failed:', error)
				}
			}),
		)
		return unsubscribe
	}, [qrType])

	return (
		<Accordion.Root
			type="multiple"
			value={openSectionList}
			onValueChange={setOpenSectionList}
			className="flex w-full flex-col gap-4"
		>
			{formSectionList.map(
				({ id, title, description, icon, Content }) => {
					return (
						<GeneratorFormSection
							key={title}
							id={id}
							title={title}
							description={description}
							icon={icon}
							content={<Content />}
							isOpen={openSectionList.includes(id)}
						/>
					)
				},
			)}
		</Accordion.Root>
	)
}
